/**
 * ProductSearchPageLayout Komponenti
 * SidebarFilters + ProductList birleştiren layout komponenti
 * Backend'de render edilebilir (data-component-path ile)
 */

'use client'
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useMemo, useState, useEffect, useLayoutEffect, useCallback, useRef, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import MainSearchBar from '@/components/search/MainSearchBar'
import SidebarFilters from '@/components/search/SidebarFilters'
import TourCard, { type TourProps } from '@/components/ui/TourCard'
import TourCardSkeleton from '@/components/ui/TourCardSkeleton'
import YachtCard from '@/components/ui/YachtCard'
import { useProductSearch } from '@/hooks/useProductSearch'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'
import { useProductCategories } from '@/contexts/ProductCategoriesContext'
import { useTravelersPreferences } from '@/contexts/TravelersPreferencesContext'
import type { Product, ProductListingInitialSsr } from '@/types/product-search'
import { parseTourDurationMeta } from '@/lib/tour-duration'
import type { YachtProps } from '@/types/generalInterFaces'
import {
    staggerContainerVariants,
    tourListContainerVariants,
    paginationVariants
} from "@/lib/animations"
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils"
import type { PageUrlMap } from "@/types/blog"
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html"

/** Geçici: true iken fiyatı olmayan (Request Info butonu çıkan) turlar listede görünmez. Tekrar göstermek için false yapın. */
const HIDE_TOURS_WITHOUT_PRICE = false

/** Path’ten son segment (çoklu dil: /en/group-tours → group-tours, /tr/grup-turlari → grup-turlari). Hardcode yok. */
function getLastPathSegment(pathname: string | null): string | undefined {
    if (!pathname) return undefined
    const segment = pathname.replace(/^\/+/, '').split('/').filter(Boolean).pop()
    return segment || undefined
}

/** Alt sayfadaysak (örn. /group-tours, /en/group-tours) o sayfaya ait slug; ana sayfadaysak undefined. */
function getCurrentPageSlugForTabs(pathname: string | null): string | undefined {
    if (!pathname) return undefined
    const segments = pathname.replace(/^\/+/, '').split('/').filter(Boolean)
    if (segments.length === 0) return undefined
    if (segments.length === 1 && segments[0].length === 2) return undefined // sadece dil kodu = ana sayfa
    return segments.length > 1 ? segments[segments.length - 1] : segments[0]
}

/** Ana kategori slug’ı: kategori sayfasındaysak categorySlug’ın ilk parçası, değilse path’in son segmenti. Sidebar’da eşleşen gizlenir. */
function getCurrentPageCategorySlugForSidebar(pathname: string | null, categorySlug: string | undefined): string | undefined {
    if (categorySlug && categorySlug.trim()) return categorySlug.split('/')[0]?.trim() || undefined
    return getLastPathSegment(pathname)
}

/** URL path'inden product type çıkar (MainSearchBar ile uyumlu). /en/tour, /en/yacht → tour, yacht. */
/** SSR-safe: lg breakpoint (Sidebar + masaüstü düzeni). */
function useMinWidthLg() {
    return useSyncExternalStore(
        (onChange) => {
            if (typeof window === 'undefined') return () => {}
            const mq = window.matchMedia('(min-width: 1024px)')
            mq.addEventListener('change', onChange)
            return () => mq.removeEventListener('change', onChange)
        },
        () => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false),
        () => false
    )
}

function getProductTypeFromPathname(pathname: string | null): string | null {
    if (!pathname) return null
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 0) return null
    const isLangCode = segments[0]?.length === 2 && /^[a-z]{2}$/.test(segments[0])
    const productTypeSegment = isLangCode ? segments[1] : segments[0]
    const validProductTypes = ['tour', 'private-tours', 'group-tours', 'yacht', 'ship_tour', 'ship', 'mice', 'cruise']
    if (productTypeSegment && validProductTypes.includes(productTypeSegment)) {
        return productTypeSegment === 'cruise' ? 'ship_tour' : productTypeSegment
    }
    return null
}

export interface ProductSearchPageLayoutProps {
    productType?: string
    categorySlug?: string
    /** Sunucuda hesaplanan landing slug (group-tours / private-tours); pathname ile uyumlu */
    landingCategorySlug?: string
    categoryName?: string
    language?: string
    languageId?: number
    initialFilters?: Record<string, any>
    /** Sunucuda çekilen ilk sayfa liste + filtre (SSR) */
    initialProductListing?: ProductListingInitialSsr | null
    className?: string
    pageUrls?: PageUrlMap
    defaultLanguage?: string
    __grapesPreview?: boolean
}

export default function ProductSearchPageLayout({
    productType: propProductType,
    categorySlug: propCategorySlug,
    landingCategorySlug: propLandingCategorySlug,
    categoryName: propCategoryName,
    language: propLanguage,
    languageId: propLanguageId,
    initialFilters: propInitialFilters,
    initialProductListing: propInitialProductListing,
    className = '',
    pageUrls: propPageUrls,
    defaultLanguage: propDefaultLanguage,
    __grapesPreview = false,
    ...restProps
}: ProductSearchPageLayoutProps) {
    const pathname = usePathname()
    const lastPathSegment = useMemo(() => getLastPathSegment(pathname), [pathname])
    const slugToHideInSidebar = useMemo(() => getCurrentPageCategorySlugForSidebar(pathname, propCategorySlug), [pathname, propCategorySlug])
    const landingCategorySlug = useMemo(
        () => propLandingCategorySlug ?? (!propCategorySlug ? lastPathSegment : undefined),
        [propLandingCategorySlug, propCategorySlug, lastPathSegment]
    )
    /** Alt sayfada sadece o sayfaya ait sekme gösterilsin diye; ana sayfada undefined. */
    const currentPageSlugForTabs = useMemo(() => getCurrentPageSlugForTabs(pathname), [pathname])
    const { getVariable, language: contextLanguage } = useLanguageVariables()
    const { preferences: travelersPreferences } = useTravelersPreferences()

    /** MainSearchBar'daki tarih ve kişi sayısı – TourCard'da seçili kriterlere göre fiyat göstermek için */
    const searchContext = useMemo(() => {
        const dep = travelersPreferences?.departure_date
        const rooms = travelersPreferences?.rooms
        if (!dep || !Array.isArray(rooms) || rooms.length === 0) return null
        const adults = rooms.reduce((s, r) => s + (r.adults ?? 0), 0)
        const children = rooms.reduce((s, r) => s + (r.children ?? 0), 0)
        if (adults < 1) return null
        return {
            searchDepartureDate: typeof dep === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dep) ? dep : null,
            searchPaxCount: adults,
            searchChildCount: children,
        }
    }, [travelersPreferences?.departure_date, travelersPreferences?.rooms])

    const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps })
    const backendAttributes = getBackendRenderAttributes(
        'page_layouts/ProductSearchPageLayout',
        { productType: propProductType, categorySlug: propCategorySlug, language: propLanguage, initialFilters: propInitialFilters },
        isBackendRender
    )

    // Product type: önce URL path'ten (client navigation için), yoksa prop'tan
    const pathnameProductType = useMemo(() => getProductTypeFromPathname(pathname), [pathname])
    const finalProductType = (pathnameProductType ?? (propProductType || 'tour')).replace(/-cat$/, '')
    const finalCategorySlug = propCategorySlug
    // Category endpoint language_id ile eşleştiği için language'ı kesin gönder.
    // Language prefix olmayan URL'lerde (örn: /yacht/...) cookie yoksa backend varsayılanı TR seçip kategori bulunamayabiliyor.
    const finalLanguage = propLanguage || contextLanguage || propDefaultLanguage || 'en'
    const {
        products,
        filters,
        isLoading,
        isFiltersLoading,
        error,
        pagination,
        sortBy,
        setSortBy,
        setPage,
        refresh,
        clearError,
        updateFilter,
        updateFilters,
        clearSidebarFilters,
        getSuggestions,
        activeFilters,
        category,
    } = useProductSearch({
        productType: finalProductType,
        categorySlug: finalCategorySlug,
        landingCategorySlug: landingCategorySlug ?? undefined,
        language: finalLanguage,
        // Kategori sayfası (finalCategorySlug) ve landing sayfası (group-tours, private-tours → categorySlug undefined) için ürün listesi yüklensin
        autoLoad: true,
        calculatePrices: true,
        perPage: 12,
        initialListing: propInitialProductListing ?? undefined,
    })

    const searchFields = useMemo(() => filters?.search_fields || [], [filters?.search_fields])
    const isSearchFieldsLoading = isFiltersLoading

    // Arama çubuğundaki sekmeler: layout context'ten veya API'den (show_in_search_tabs=1)
    const contextCategories = useProductCategories()
    const toTabItem = (c: { id: number; name: string; slug: string; type?: string; search_tab_title?: string; search_tab_icon?: string }) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        type: (c.type || 'tour') as string,
        tabTitle: c.search_tab_title ?? undefined,
        tabIconUrl: c.search_tab_icon ?? undefined,
    })
    const [categoryTabs, setCategoryTabs] = useState<Array<{ id: number; name: string; slug: string; type: string; tabTitle?: string; tabIconUrl?: string }>>(() =>
        contextCategories.length > 0 ? contextCategories.map(toTabItem) : []
    )
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
    useEffect(() => {
        if (contextCategories.length > 0) {
            const list = contextCategories.map(toTabItem)
            setCategoryTabs(list)
            if (list.length > 0) setSelectedCategoryId((prev) => (prev == null ? list[0].id : prev))
            return
        }
        let cancelled = false
        const load = async () => {
            try {
                const params = new URLSearchParams()
                params.set('language', finalLanguage)
                params.set('is_active', '1')
                params.set('show_in_search_tabs', '1')
                const res = await fetch(`/api/product-categories?${params.toString()}`)
                if (!res.ok) {
                    if (!cancelled) setCategoryTabs([])
                    return
                }
                const data = await res.json()
                const list = (data?.data ?? []).map((c: { id: number; name: string; slug: string; type?: string; search_tab_title?: string; search_tab_icon?: string }) => toTabItem(c))
                if (!cancelled) {
                    setCategoryTabs(list)
                    if (list.length > 0) setSelectedCategoryId((prev) => (prev == null ? list[0].id : prev))
                }
            } catch {
                if (!cancelled) setCategoryTabs([])
            }
        }
        load()
        return () => { cancelled = true }
    }, [finalLanguage, contextCategories])

    // Alt sayfada (örn. /group-tours) seçili kategoriyi URL slug ile eşleştir
    const currentPageSlugForTabsRef = useRef(currentPageSlugForTabs)
    currentPageSlugForTabsRef.current = currentPageSlugForTabs
    useEffect(() => {
        if (!currentPageSlugForTabsRef.current || categoryTabs.length === 0) return
        const match = categoryTabs.find((c) => c.slug === currentPageSlugForTabsRef.current)
        if (match) setSelectedCategoryId(match.id)
    }, [categoryTabs, currentPageSlugForTabs])

    const handleSearch = useCallback(
        (searchFilters: Record<string, any>) => {
            const cleanedFilters: Record<string, any> = {}
            Object.entries(searchFilters).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    cleanedFilters[key] = value
                }
            })
            updateFilters(cleanedFilters)
            // MainSearchBar çerez/context'ten gelen filtrelerle hemen istek at (state güncellenmeden eski filtreyle gitmesin)
            refresh(cleanedFilters)
        },
        [updateFilters, refresh]
    )

    const isLg = useMinWidthLg()
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
    const [filterPortalReady, setFilterPortalReady] = useState(false)
    useEffect(() => {
        setFilterPortalReady(true)
    }, [])

    /** Mobil/tablet: varsayılan kutu görünümü; lg+: liste (yatay). Kullanıcı değiştirdiyse yeniden boyutlandırmada ezme. */
    const userChoseViewTypeRef = useRef(false)
    const [viewType, setViewType] = useState<'box' | 'horizontal'>('box')

    useLayoutEffect(() => {
        const mq = window.matchMedia('(min-width: 1024px)')
        const sync = () => {
            if (!userChoseViewTypeRef.current) {
                setViewType(mq.matches ? 'horizontal' : 'box')
            }
        }
        sync()
        mq.addEventListener('change', sync)
        return () => mq.removeEventListener('change', sync)
    }, [])

    const handleViewTypeChange = useCallback((newViewType: 'box' | 'horizontal') => {
        userChoseViewTypeRef.current = true
        if (newViewType !== viewType) {
            setViewType(newViewType)
        }
    }, [viewType])

    useEffect(() => {
        if (isLg) setMobileFiltersOpen(false)
    }, [isLg])

    useEffect(() => {
        if (!mobileFiltersOpen) return
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = prev
        }
    }, [mobileFiltersOpen])

    useEffect(() => {
        if (!mobileFiltersOpen) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileFiltersOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [mobileFiltersOpen])

    // Sadece setSortBy; useProductSearch içindeki useEffect sortBy değişince tek sefer yükleme yapacak.
    // refresh() çağrısı kaldırıldı (çift yükleme ve placeholder tekrarlarını önlemek için)
    const handleSortChange = useCallback((sortType: 'price_asc' | 'price_desc' | 'duration_asc' | 'duration_desc') => {
        setSortBy(sortType)
    }, [setSortBy])

    // Helper: Parse images array from various formats
    const parseImagesArray = useCallback((value: unknown): string[] => {
        if (!value) return []
        if (Array.isArray(value)) return value
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value)
                return Array.isArray(parsed) ? parsed : [value]
            } catch {
                return [value]
            }
        }
        return []
    }, [])

    // Helper: Parse image alt texts array
    const parseImageAltArray = useCallback((value: unknown, fallbackName: string, imageCount: number): string[] => {
        if (!value) {
            return imageCount > 0 ? Array(imageCount).fill(fallbackName) : []
        }
        if (Array.isArray(value)) return value
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value)
                const result = Array.isArray(parsed) ? parsed : [value]
                return result.length > 0 ? result : Array(imageCount).fill(fallbackName)
            } catch {
                return [value]
            }
        }
        return imageCount > 0 ? Array(imageCount).fill(fallbackName) : []
    }, [])

    /** Kart başına gösterilecek maksimum resim (performans için) */
    const MAX_IMAGES_PER_TOUR_CARD = 10

    // Product type'a göre product'ları doğru formata çevir
    const mapProductToTourProps = useCallback((product: Product): TourProps => {
        const allImages = parseImagesArray(product.metas?.images)
        const images = allImages.slice(0, MAX_IMAGES_PER_TOUR_CARD)
        const imageAlt = parseImageAltArray(product.metas?.image_alt, product.name, images.length)
        
        const productId = product.metas?.product_id ?? product.id ?? null
        const finalPrice = product.calculated_price?.success && product.calculated_price.final_price > 0
            ? product.calculated_price.final_price
            : 0
        const defaultSymbol = filters?.default_currency_symbol ?? '€'
        const currency = product.calculated_price?.currency || product.currency || {
            symbol: defaultSymbol,
            code: 'EUR',
            symbol_position: 'before',
            thousands_separator: ',',
            decimal_separator: '.',
        }
        const currencySymbol = currency.symbol || defaultSymbol
        const transfer = parseImagesArray(product.metas?.transfer) as string[]
        
        let tourDurationDay: number | undefined
        let tourDurationNight: number | undefined
        if (product.metas?.tour_duration) {
            let durationData = product.metas.tour_duration
            if (typeof durationData === 'string') {
                try {
                    durationData = JSON.parse(durationData)
                } catch {
                    // Ignore parse errors
                }
            }
            if (typeof durationData === 'object' && durationData !== null) {
                tourDurationDay = durationData.day !== undefined ? Number(durationData.day) : undefined
                tourDurationNight = durationData.night !== undefined ? Number(durationData.night) : undefined
            }
        }
        
        let tourRoute: Array<{
            location_type?: string
            location_title?: string
            location_description?: string
            location_address?: string
        }> = []
        // Öncelik: Başlangıç ili, ara duraklar, bitiş ili (departure_city, station_city, end_city) — sync ile gelen doğru veri
        const departureCity = product.metas?.departure_city != null ? String(product.metas.departure_city).trim() : ''
        const endCity = product.metas?.end_city != null ? String(product.metas.end_city).trim() : ''
        let stationCity: string[] = []
        if (product.metas?.station_city != null) {
            const raw = product.metas.station_city
            if (Array.isArray(raw)) {
                stationCity = raw.map((s: unknown) => (s && typeof s === 'string' ? s.trim() : String(s).trim())).filter(Boolean)
            } else if (typeof raw === 'string') {
                try {
                    const parsed = JSON.parse(raw)
                    stationCity = Array.isArray(parsed)
                        ? parsed.map((s: unknown) => (s && typeof s === 'string' ? s.trim() : (s && typeof s === 'object' && s !== null && 'value' in s ? String((s as { value?: unknown }).value).trim() : String(s).trim()))).filter(Boolean)
                        : []
                } catch {
                    stationCity = [raw.trim()].filter(Boolean)
                }
            }
        }
        if (departureCity || stationCity.length > 0 || endCity) {
            const parts: Array<{ location_title?: string }> = []
            if (departureCity) parts.push({ location_title: departureCity })
            stationCity.forEach((s) => parts.push({ location_title: s }))
            if (endCity) parts.push({ location_title: endCity })
            if (parts.length > 0) tourRoute = parts
        }
        if (tourRoute.length === 0 && product.metas?.tour_route) {
            let routeData = product.metas.tour_route
            if (typeof routeData === 'string') {
                try {
                    routeData = JSON.parse(routeData)
                } catch {
                    routeData = []
                }
            }
            if (Array.isArray(routeData)) {
                tourRoute = routeData
            }
        }
        // tour_route boşsa, tour_program içinden rota üret (yeni sistem uyumluluğu)
        if (tourRoute.length === 0 && product.metas?.tour_program) {
            let programData = product.metas.tour_program
            if (typeof programData === 'string') {
                try {
                    programData = JSON.parse(programData)
                } catch {
                    programData = []
                }
            }
            if (Array.isArray(programData)) {
                const derivedRoute = programData
                    .flatMap((dayItem: unknown) => {
                        if (!dayItem || typeof dayItem !== 'object') return null
                        const day = dayItem as Record<string, unknown>
                        const locationTitle = typeof day.location_title === 'string' ? day.location_title : ''
                        const locationAddress = typeof day.location_address === 'string' ? day.location_address : ''
                        const dayTitle = typeof day.title === 'string' ? day.title : ''

                        const mainLocation = (!locationTitle && !locationAddress && !dayTitle)
                            ? null
                            : {
                            location_type: typeof day.location_type === 'string' ? day.location_type : 'waypoints',
                            location_title: locationTitle || dayTitle,
                            location_description: typeof day.location_description === 'string' ? day.location_description : '',
                            location_address: locationAddress,
                        }

                        const additionalLocations = Array.isArray(day.additional_locations)
                            ? day.additional_locations
                                .map((loc) => {
                                    if (!loc || typeof loc !== 'object') return null
                                    const location = loc as Record<string, unknown>
                                    const extraTitle = typeof location.location_title === 'string' ? location.location_title : ''
                                    const extraAddress = typeof location.location_address === 'string' ? location.location_address : ''
                                    if (!extraTitle && !extraAddress) return null
                                    return {
                                        location_type: typeof location.location_type === 'string' ? location.location_type : 'waypoints',
                                        location_title: extraTitle,
                                        location_description: typeof location.location_description === 'string' ? location.location_description : '',
                                        location_address: extraAddress,
                                    }
                                })
                                .filter(Boolean)
                            : []

                        return [mainLocation, ...additionalLocations]
                    })
                    .flat()
                    .filter(Boolean) as Array<{
                        location_type?: string
                        location_title?: string
                        location_description?: string
                        location_address?: string
                    }>
                tourRoute = derivedRoute
            }
        }
        
        let pricingPeriods: Array<{
            period_name?: string
            period_start?: string
            period_end?: string
            quota?: number
            packages?: Array<{
                package_name?: string
                prices?: Array<{
                    pax_range_id?: number
                    price_per_person?: number
                    single_supplement?: number
                    extra_bed_price?: number
                }>
            }>
        }> = []
        if (product.metas?.tour_pricing) {
            let pricingData = product.metas.tour_pricing
            if (typeof pricingData === 'string') {
                try {
                    pricingData = JSON.parse(pricingData)
                } catch {
                    pricingData = []
                }
            }
            if (Array.isArray(pricingData)) {
                pricingPeriods = pricingData
            }
        }

        let groupDepartureDates: Array<{
            period_id?: number | null
            departure_date?: string
            quota?: number | null
            status?: string
            discount_percentage?: number | null
        }> = []
        if (Array.isArray(product.metas?.initial_tour_departure_dates)) {
            groupDepartureDates = product.metas.initial_tour_departure_dates
                .map((item: unknown) => {
                    if (!item || typeof item !== 'object') return null
                    const row = item as Record<string, unknown>
                    return {
                        period_id: typeof row.period_id === 'number' ? row.period_id : (row.period_id == null ? null : Number(row.period_id)),
                        departure_date: typeof row.departure_date === 'string' ? row.departure_date : undefined,
                        quota: row.quota == null ? null : Number(row.quota),
                        status: typeof row.status === 'string' ? row.status : undefined,
                        discount_percentage: row.discount_percentage == null ? null : Number(row.discount_percentage),
                    }
                })
                .filter(Boolean) as Array<{
                period_id?: number | null
                departure_date?: string
                quota?: number | null
                status?: string
                discount_percentage?: number | null
            }>
        }
        
        return {
            src: images,
            alt: imageAlt.length > 0 ? imageAlt : [product.name],
            type: product.metas?.type || product.type,
            imageCount: allImages.length || (product.metas?.image_count ? parseInt(String(product.metas.image_count)) : 0),
            videoCount: product.metas?.video_count ? parseInt(String(product.metas.video_count)) : 0,
            title: product.name,
            transferType: product.metas?.transfer_type || '',
            start: product.metas?.start_location || product.metas?.departure_city || '',
            stops: product.metas?.stops ?? (() => {
                const s = product.metas?.station_city;
                if (s == null) return '';
                if (Array.isArray(s)) return s.join(', ');
                if (typeof s === 'string') {
                    try {
                        const a = JSON.parse(s);
                        return Array.isArray(a) ? a.join(', ') : s;
                    } catch {
                        return s;
                    }
                }
                return String(s);
            })(),
            end: product.metas?.end_location || product.metas?.end_city || '',
            tourDuration: parseTourDurationMeta(product.metas?.tour_duration).label,
            tourType: product.metas?.tour_type || product.metas?.group_type || '',
            tag: product.is_featured ? 'Featured' : '',
            is_featured: product.is_featured || false,
            price: {
                discount_price: finalPrice > 0 ? `${currencySymbol}${finalPrice.toFixed(2)}` : `${currencySymbol}0.00`,
                price: product.calculated_price?.base_price ? `${currencySymbol}${product.calculated_price.base_price.toFixed(2)}` : `${currencySymbol}${finalPrice.toFixed(2)}`,
            },
            calculated_price: product.calculated_price,
            base_price: product.base_price, // En düşük fiyat (tarih ve kişi sayısı yoksa)
            currency: product.currency ? {
                id: product.currency.id,
                code: product.currency.code,
                symbol: product.currency.symbol,
                symbol_position: product.calculated_price?.currency?.symbol_position || 'after',
                thousands_separator: product.calculated_price?.currency?.thousands_separator || ',',
                decimal_separator: product.calculated_price?.currency?.decimal_separator || '.',
            } : null,
            reviews: product.metas?.reviews_count ? parseInt(String(product.metas.reviews_count)) : 0,
            tourId: productId ? String(productId) : (product.slug || 'unknown'),
            productId: productId ? String(productId) : String(product.id),
            brochureProductId: product.id,
            slug: product.slug,
            transfer: transfer.length > 0 ? transfer : undefined,
            tourDurationDay,
            tourDurationNight,
            tourRoute: tourRoute.length > 0 ? tourRoute : undefined,
            pricingPeriods: pricingPeriods.length > 0 ? pricingPeriods : undefined,
            groupDepartureDates: groupDepartureDates.length > 0 ? groupDepartureDates : undefined,
            booking_flow: (() => {
                const raw = product.metas?.booking_flow
                if (raw === 'instant_online' || raw === 'bank_transfer_only' || raw === 'request_first') return raw
                return undefined
            })(),
        }
    }, [parseImagesArray, filters?.default_currency_symbol])

    const mapProductToYachtProps = useCallback((product: Product): YachtProps => {
        const images = parseImagesArray(product.metas?.images)
        const imageAlt = parseImageAltArray(product.metas?.image_alt, product.name, images.length)
        
        const medias = images.map((img: string, index: number) => ({
            src: img,
            alt: imageAlt[index] || product.name,
        }))
        
        // Fiyat hesaplama: sadece calculated_price kullan
        const finalPrice = product.calculated_price?.success && product.calculated_price.final_price > 0
            ? product.calculated_price.final_price
            : 0
        
        const defaultSymbol = filters?.default_currency_symbol ?? '€'
        const currencySymbol = product.currency?.symbol || product.calculated_price?.currency?.symbol || defaultSymbol

        // Currency bilgisi: önce calculated_price'tan, sonra product.currency'den
        const rawCurrency = product.calculated_price?.currency || product.currency
        const currency = rawCurrency ? {
            id: (rawCurrency as any).id || 0,
            code: (rawCurrency as any).code || 'EUR',
            name: (rawCurrency as any).name || 'Euro',
            symbol: (rawCurrency as any).symbol || currencySymbol,
            symbol_position: (rawCurrency as any).symbol_position || 'before',
            thousands_separator: (rawCurrency as any).thousands_separator || ',',
            decimal_separator: (rawCurrency as any).decimal_separator || '.',
        } : {
            id: 0,
            code: 'EUR',
            name: 'Euro',
            symbol: currencySymbol,
            symbol_position: 'before',
            thousands_separator: ',',
            decimal_separator: '.',
        }

        const minRentalDuration = (product.calculated_price as any)?.min_rental_duration 
            ? String((product.calculated_price as any).min_rental_duration)
            : (product.metas?.min_rental_duration ? String(product.metas.min_rental_duration) : '')

        const guestRaw =
            product.metas?.guest_capacity ??
            product.metas?.passenger_capacity ??
            product.metas?.yacht_guest_capacity ??
            product.metas?.capacity ??
            product.metas?.pax_count
        const guestCapacity =
            guestRaw != null && guestRaw !== ''
                ? (() => {
                      const n = parseInt(String(guestRaw), 10)
                      return Number.isNaN(n) ? undefined : n
                  })()
                : undefined
        let startLocation = product.metas?.yacht_marina || product.metas?.start_location || ''
        if (startLocation && typeof startLocation === 'string' && startLocation.startsWith('[')) {
            try {
                const parsed = JSON.parse(startLocation)
                if (Array.isArray(parsed) && parsed.length > 0) {
                    startLocation = parsed[0]
                } else if (typeof parsed === 'string') {
                    startLocation = parsed
                }
            } catch {
                // Ignore parse errors
            }
        }

        return {
            medias,
            title: product.name,
            start: startLocation,
            rentType: product.metas?.rental_type || '',
            minRentDay: minRentalDuration,
            length: product.metas?.length || product.metas?.yacht_length || '',
            cabin: product.metas?.cabin_count || product.metas?.cabin || '',
            wc: product.metas?.bathroom_wc || product.metas?.wc ? parseInt(String(product.metas.bathroom_wc || product.metas.wc)) : 0,
            guestCapacity: guestCapacity,
            yachtClass: product.metas?.yacht_class || undefined,
            yachtType: product.metas?.yacht_type || undefined,
            builtYear: product.metas?.built_refit_year ? String(product.metas.built_refit_year) : undefined,
            beam: product.metas?.yacht_beam || undefined,
            flag: product.metas?.yacht_flag || undefined,
            brand: product.metas?.yacht_brand || undefined,
            price: {
                price: finalPrice > 0 ? `${currencySymbol}${finalPrice.toFixed(2)}` : `${currencySymbol}0.00`,
                type: product.metas?.price_type || 'Day',
            },
            code: product.metas?.code || `YACHT-${product.id}`,
            productId: product.id,
            imageCount: images.length || (product.metas?.image_count ? parseInt(String(product.metas.image_count)) : 0),
            videoCount: product.metas?.video_count ? parseInt(String(product.metas.video_count)) : 0,
            slug: product.slug,
            calculated_price: product.calculated_price ? {
                ...product.calculated_price,
                currency: currency,
                min_rental_duration: (product.calculated_price as any).min_rental_duration 
                    ? (product.calculated_price as any).min_rental_duration 
                    : (product.metas?.min_rental_duration ? parseInt(String(product.metas.min_rental_duration)) : undefined),
                used_months: (product.calculated_price as any).used_months || undefined,
            } : undefined,
        }
    }, [parseImagesArray, parseImageAltArray, filters?.default_currency_symbol])

    // Product type'a göre mapped products
    const mappedProducts = useMemo(() => {
        if (!Array.isArray(products) || products.length === 0) {
            return []
        }

        let list: Product[] = products
        if (finalProductType === 'tour' && HIDE_TOURS_WITHOUT_PRICE) {
            list = products.filter(
                (p) => p.calculated_price?.success === true && (p.calculated_price?.final_price ?? 0) > 0
            )
        }

        if (finalProductType === 'yacht') {
            return list.map(mapProductToYachtProps)
        }
        // Default: tour - Featured turları üste sırala
        const mapped = list.map(mapProductToTourProps)
        return mapped.sort((a, b) => {
            if (a.is_featured && !b.is_featured) return -1
            if (!a.is_featured && b.is_featured) return 1
            return 0
        })
    }, [products, finalProductType, mapProductToTourProps, mapProductToYachtProps])

    return (
        <motion.main
            {...backendAttributes}
            initial="hidden"
            animate="visible"
            variants={staggerContainerVariants}
        >
            <MainSearchBar
                productType={finalProductType}
                searchFields={searchFields}
                language={finalLanguage}
                categorySlug={finalCategorySlug ?? currentPageSlugForTabs}
                categoryName={propCategoryName}
                onSearch={handleSearch}
                isLoading={isSearchFieldsLoading}
                pageUrls={propPageUrls}
                defaultLanguage={propDefaultLanguage}
                categoryTabs={categoryTabs}
                selectedCategoryId={selectedCategoryId}
                onCategoryTabChange={(cat) => setSelectedCategoryId(cat?.id ?? null)}
                multiTab={!currentPageSlugForTabs && categoryTabs.length > 1}
            />

            <div className="w-full lg:w-9/12 mx-auto px-4 lg:px-0 mt-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-10">
                    {isLg ? (
                        <div className="lg:col-span-3">
                            <SidebarFilters
                                filters={filters}
                                activeFilters={activeFilters}
                                updateFilter={updateFilter}
                                getSuggestions={getSuggestions}
                                isFiltersLoading={isFiltersLoading}
                                clearSidebarFilters={clearSidebarFilters}
                                hideCategorySlugs={slugToHideInSidebar ? [slugToHideInSidebar] : undefined}
                                hideEntireCategoryFilterWhenMainCategory={!propCategorySlug && !!slugToHideInSidebar}
                                hideLocationGroup
                                hideFieldKeys={
                                    finalProductType === 'tour' ||
                                    finalProductType === 'private-tours' ||
                                    finalProductType === 'group-tours'
                                        ? ['tour_type']
                                        : undefined
                                }
                            />
                        </div>
                    ) : null}
                    <div className={isLg ? 'lg:col-span-9' : 'col-span-full'}>
                        <div className="flex flex-col gap-5">
                            <div className="p-4 bg-white border border-[#E7EBF3] rounded-lg">
                                <div className="flex flex-col gap-4">
                                    {/* lg: tek satır — sonuç metni | sıralama | görünüm; küçük ekranda sıralama alt satırda */}
                                    <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-end lg:gap-4 xl:gap-6">
                                        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 lg:max-w-[min(100%,28rem)] lg:shrink-0">
                                        <h3 className="min-w-0 font-semibold text-alsero-dark-gray">
                                            {getVariable('search_showing_results', 'Showing')}{' '}
                                            <b>{pagination ? `${mappedProducts.length} of ${pagination.total}` : mappedProducts.length}</b>{' '}
                                            {getVariable('search_results', 'Results')}
                                        </h3>
                                            {!isLg ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setMobileFiltersOpen(true)}
                                                    className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full border-2 border-alsero-first/35 bg-alsero-first/5 px-4 py-2 text-sm font-semibold text-alsero-first shadow-sm transition-colors hover:bg-alsero-first hover:text-white focus:outline-none focus:ring-2 focus:ring-alsero-first/30"
                                                    aria-expanded={mobileFiltersOpen}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                                                        <path d="M4 6h16M7 12h10M10 18h4" />
                                                    </svg>
                                                    {getVariable('search_open_filters', 'Filters')}
                                                </button>
                                            ) : null}
                                        </div>

                                    <div className="flex min-w-0 w-full flex-col gap-3 border-t border-alsero-light-gray/20 pt-3 lg:ml-auto lg:w-auto lg:flex-row lg:items-end lg:justify-end lg:gap-4 lg:border-t-0 lg:pt-0 xl:gap-6">
                                    <div
                                        className={`min-w-0 grid gap-3 sm:gap-3 lg:flex lg:shrink-0 lg:flex-row lg:flex-nowrap lg:items-center lg:justify-end lg:gap-4 ${
                                            finalProductType === 'tour' ||
                                            finalProductType === 'private-tours' ||
                                            finalProductType === 'group-tours'
                                                ? 'grid-cols-1 sm:grid-cols-2'
                                                : 'grid-cols-1 w-full max-w-md'
                                        }`}
                                    >
                                        {/* Mobilde: etiket üstte + pill tam genişlik; lg+: flex-row */}
                                        <div className="flex min-w-0 flex-col gap-1.5 lg:flex-row lg:items-center lg:gap-2 xl:gap-3">
                                            <span className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-alsero-dark-gray/80 sm:text-xs lg:whitespace-nowrap">
                                                {getVariable('search_sort_price_label', 'Price')}
                                            </span>
                                            <div className="relative flex w-full min-w-0 rounded-full bg-alsero-light-gray/25 p-0.5 shadow-inner lg:min-w-[11rem] lg:flex-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSortChange('price_desc')}
                                                    className={`relative z-10 flex min-h-[40px] flex-1 items-center justify-center gap-1 px-2 text-xs font-medium whitespace-nowrap rounded-full cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-alsero-first/30 sm:min-h-[44px] sm:px-3 sm:text-sm ${
                                                        sortBy === 'price_desc'
                                                            ? 'text-alsero-first bg-white shadow-sm'
                                                            : 'text-alsero-dark-gray/70 hover:text-alsero-dark-gray'
                                                    }`}
                                                >
                                                    <svg className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 2v8M3 5l3-3 3 3"/></svg>
                                                    <span className="whitespace-nowrap">{getVariable('search_sort_desc_short', 'Desc')}</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSortChange('price_asc')}
                                                    className={`relative z-10 flex min-h-[40px] flex-1 items-center justify-center gap-1 px-2 text-xs font-medium whitespace-nowrap rounded-full cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-alsero-first/30 sm:min-h-[44px] sm:px-3 sm:text-sm ${
                                                        sortBy === 'price_asc'
                                                            ? 'text-alsero-first bg-white shadow-sm'
                                                            : 'text-alsero-dark-gray/70 hover:text-alsero-dark-gray'
                                                    }`}
                                                >
                                                    <svg className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 10V2M3 7l3 3 3-3"/></svg>
                                                    <span className="whitespace-nowrap">{getVariable('search_sort_asc_short', 'Asc')}</span>
                                                </button>
                                            </div>
                                        </div>
                                        {(finalProductType === 'tour' || finalProductType === 'private-tours' || finalProductType === 'group-tours') && (
                                            <div className="flex min-w-0 flex-col gap-1.5 lg:flex-row lg:items-center lg:gap-2 xl:gap-3">
                                                <span className="shrink-0 text-[10px] font-medium uppercase tracking-wider text-alsero-dark-gray/80 sm:text-xs lg:whitespace-nowrap">
                                                    {getVariable('search_sort_days_label', 'Days')}
                                                </span>
                                                <div className="relative flex w-full min-w-0 rounded-full bg-alsero-light-gray/25 p-0.5 shadow-inner lg:min-w-[11rem] lg:flex-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSortChange('duration_desc')}
                                                        className={`relative z-10 flex min-h-[40px] flex-1 items-center justify-center gap-1 px-2 text-xs font-medium whitespace-nowrap rounded-full cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-alsero-first/30 sm:min-h-[44px] sm:px-3 sm:text-sm ${
                                                            sortBy === 'duration_desc'
                                                                ? 'text-alsero-first bg-white shadow-sm'
                                                                : 'text-alsero-dark-gray/70 hover:text-alsero-dark-gray'
                                                        }`}
                                                    >
                                                        <svg className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 2v8M3 5l3-3 3 3"/></svg>
                                                        <span className="whitespace-nowrap">{getVariable('search_sort_desc_short', 'Desc')}</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSortChange('duration_asc')}
                                                        className={`relative z-10 flex min-h-[40px] flex-1 items-center justify-center gap-1 px-2 text-xs font-medium whitespace-nowrap rounded-full cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-alsero-first/30 sm:min-h-[44px] sm:px-3 sm:text-sm ${
                                                            sortBy === 'duration_asc'
                                                                ? 'text-alsero-first bg-white shadow-sm'
                                                                : 'text-alsero-dark-gray/70 hover:text-alsero-dark-gray'
                                                        }`}
                                                    >
                                                        <svg className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 10V2M3 7l3 3 3-3"/></svg>
                                                        <span className="whitespace-nowrap">{getVariable('search_sort_asc_short', 'Asc')}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                        <div
                                            className="hidden shrink-0 flex-row items-center gap-4 lg:flex"
                                            role="group"
                                            aria-label={getVariable('search_view_mode', 'View mode')}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleViewTypeChange('horizontal')}
                                                className={`flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-alsero-light-gray/20 focus:outline-none focus:ring-2 focus:ring-alsero-first/30 ${viewType === 'horizontal' ? '[&>svg]:fill-alsero-first' : '[&>svg]:fill-alsero-dark-gray'}`}
                                                aria-pressed={viewType === 'horizontal'}
                                                aria-label={getVariable('search_view_list', 'List view')}
                                            >
                                                <svg width="25" height="17" viewBox="0 0 25 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                                    <path d="M22.9658 15.8911H7.08356C6.42898 15.8911 5.89844 15.4204 5.89844 14.8396C5.89844 14.2588 6.42898 13.7881 7.08356 13.7881H22.9654C23.62 13.7881 24.1505 14.2588 24.1505 14.8396C24.1505 15.4204 23.6204 15.8911 22.9658 15.8911Z"/>
                                                    <path d="M22.9658 9.29053H7.08356C6.42898 9.29053 5.89844 8.8198 5.89844 8.23901C5.89844 7.65823 6.42898 7.1875 7.08356 7.1875H22.9654C23.62 7.1875 24.1505 7.65823 24.1505 8.23901C24.1509 8.8198 23.6204 9.29053 22.9658 9.29053Z"/>
                                                    <path d="M22.9658 2.68994H7.08356C6.42898 2.68994 5.89844 2.21921 5.89844 1.63843C5.89844 1.05764 6.42898 0.586914 7.08356 0.586914H22.9654C23.62 0.586914 24.1505 1.05764 24.1505 1.63843C24.1505 2.21921 23.6204 2.68994 22.9658 2.68994Z"/>
                                                    <path d="M1.99006 3.13198C2.86909 3.13198 3.58168 2.49973 3.58168 1.7198C3.58168 0.939873 2.86909 0.307617 1.99006 0.307617C1.11103 0.307617 0.398438 0.939873 0.398438 1.7198C0.398438 2.49973 1.11103 3.13198 1.99006 3.13198Z"/>
                                                    <path d="M1.99006 9.65054C2.86909 9.65054 3.58168 9.01828 3.58168 8.23835C3.58168 7.45843 2.86909 6.82617 1.99006 6.82617C1.11103 6.82617 0.398438 7.45843 0.398438 8.23835C0.398438 9.01828 1.11103 9.65054 1.99006 9.65054Z"/>
                                                    <path d="M1.99006 16.1701C2.86909 16.1701 3.58168 15.5378 3.58168 14.7579C3.58168 13.978 2.86909 13.3457 1.99006 13.3457C1.11103 13.3457 0.398438 13.978 0.398438 14.7579C0.398438 15.5378 1.11103 16.1701 1.99006 16.1701Z"/>
                                                </svg>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleViewTypeChange('box')}
                                                className={`flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg transition-colors hover:bg-alsero-light-gray/20 focus:outline-none focus:ring-2 focus:ring-alsero-first/30 ${viewType === 'box' ? '[&>svg]:fill-alsero-first' : '[&>svg]:fill-alsero-dark-gray'}`}
                                                aria-pressed={viewType === 'box'}
                                                aria-label={getVariable('search_view_grid', 'Grid view')}
                                            >
                                                <svg width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                                    <path d="M7.64998 0.546875H3.01795C1.73187 0.546875 0.685547 1.52274 0.685547 2.7222V7.04227C0.685547 8.24173 1.73187 9.21759 3.01795 9.21759H7.64998C8.93606 9.21759 9.98239 8.24173 9.98239 7.04227V2.7222C9.98239 1.52274 8.93606 0.546875 7.64998 0.546875ZM8.40665 7.04227C8.40665 7.43139 8.0672 7.74798 7.64998 7.74798H3.01795C2.60074 7.74798 2.26128 7.43139 2.26128 7.04227V2.7222C2.26128 2.33308 2.60074 2.01649 3.01795 2.01649H7.64998C8.0672 2.01649 8.40665 2.33308 8.40665 2.7222V7.04227Z" />
                                                    <path d="M19.8121 0.546875H15.2425C13.9392 0.546875 12.8789 1.53578 12.8789 2.7513V7.01317C12.8789 8.22869 13.9392 9.21759 15.2425 9.21759H19.8121C21.1154 9.21759 22.1757 8.22869 22.1757 7.01317V2.7513C22.1757 1.53578 21.1154 0.546875 19.8121 0.546875ZM20.6 7.01317C20.6 7.41835 20.2466 7.74798 19.8121 7.74798H15.2425C14.8081 7.74798 14.4546 7.41835 14.4546 7.01317V2.7513C14.4546 2.34612 14.8081 2.01649 15.2425 2.01649H19.8121C20.2466 2.01649 20.6 2.34612 20.6 2.7513V7.01317Z"/>
                                                    <path d="M7.64998 11.9189H3.01795C1.73187 11.9189 0.685547 12.8948 0.685547 14.0943V18.4143C0.685547 19.6138 1.73187 20.5897 3.01795 20.5897H7.64998C8.93606 20.5897 9.98239 19.6138 9.98239 18.4143V14.0943C9.98239 12.8948 8.93606 11.9189 7.64998 11.9189ZM8.40665 18.4143C8.40665 18.8035 8.0672 19.1201 7.64998 19.1201H3.01795C2.60074 19.1201 2.26128 18.8035 2.26128 18.4143V14.0943C2.26128 13.7052 2.60074 13.3886 3.01795 13.3886H7.64998C8.0672 13.3886 8.40665 13.7052 8.40665 14.0943V18.4143Z"/>
                                                    <path d="M19.8121 11.9189H15.2425C13.9392 11.9189 12.8789 12.9078 12.8789 14.1234V18.3852C12.8789 19.6008 13.9392 20.5897 15.2425 20.5897H19.8121C21.1154 20.5897 22.1757 19.6008 22.1757 18.3852V14.1234C22.1757 12.9078 21.1154 11.9189 19.8121 11.9189ZM20.6 18.3852C20.6 18.7904 20.2466 19.1201 19.8121 19.1201H15.2425C14.8081 19.1201 14.4546 18.7904 14.4546 18.3852V14.1234C14.4546 13.7182 14.8081 13.3886 15.2425 13.3886H19.8121C20.2466 13.3886 20.6 13.7182 20.6 14.1234V18.3852Z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </div>
                            
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mb-6 p-6 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-lg shadow-sm"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <svg
                                                className="w-6 h-6 text-red-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                                />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-lg font-semibold text-red-800 mb-1">
                                                {getVariable('error_title', 'An error occurred')}
                                            </h4>
                                            <p className="text-red-700 mb-3">
                                                {error}
                                            </p>
                                            <div className="flex gap-3">
                                                <button type="button"
                                                    onClick={() => window.location.reload()}
                                                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm font-medium"
                                                >
                                                    {getVariable('error_reload_page', 'Reload Page')}
                                                </button>
                                                <button type="button"
                                                    onClick={() => {
                                                        // Filtreleri temizle ve yeniden yükle
                                                        if (refresh) {
                                                            refresh();
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                                                >
                                                    {getVariable('error_try_again', 'Try Again')}
                                                </button>
                                            </div>
                                        </div>
                                        <button type="button"
                                            onClick={clearError}
                                            className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors"
                                            aria-label="Hata mesajını kapat"
                                        >
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                            {isLoading ? (
                                <motion.div
                                    variants={tourListContainerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className={`grid gap-6 mb-20 ${
                                        viewType === 'box'
                                            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                            : 'grid-cols-1'
                                    }`}
                                >
                                    {[1, 2, 3, 4, 5, 6].map((i) => (
                                        <TourCardSkeleton key={i} index={i} viewType={viewType} />
                                    ))}
                                </motion.div>
                            ) : mappedProducts.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <p className="text-lg text-alsero-dark-gray font-medium">
                                        {getVariable('search_no_products_found', 'No products found')}
                                    </p>
                                    <p className="text-sm text-alsero-light-gray mt-2">
                                        {getVariable('search_adjust_filters', 'Try adjusting your filters to see more results.')}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <motion.div
                                        variants={tourListContainerVariants}
                                        initial="hidden"
                                        animate="visible"
                                        className={`grid gap-6 mb-20 ${
                                            viewType === 'box'
                                                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                                                : 'grid-cols-1'
                                        }`}
                                    >
                                        {mappedProducts.map((product, index) => {
                                            if (finalProductType === 'yacht') {
                                                const yacht = product as YachtProps
                                                return (
                                                    <YachtCard
                                                        key={`yacht-${yacht.slug || index}-${viewType}`}
                                                        yacht={yacht}
                                                        viewType={viewType}
                                                        index={index}
                                                        language={finalLanguage}
                                                        languageCode={finalLanguage}
                                                        languageId={propLanguageId}
                                                        pageUrls={propPageUrls}
                                                        defaultLanguage={propDefaultLanguage}
                                                    />
                                                )
                                            }
                                            const tour = product as TourProps
                                            return (
                                                <TourCard
                                                    key={`tour-${index}-${tour.productId || tour.slug}-${viewType}`}
                                                    tour={tour}
                                                    viewType={viewType}
                                                    index={index}
                                                    language={finalLanguage}
                                                    languageCode={finalLanguage}
                                                    languageId={propLanguageId}
                                                    pageUrls={propPageUrls}
                                                    defaultLanguage={propDefaultLanguage}
                                                    productTypeForUrl={
                                                        (finalProductType === 'group-tours' || finalProductType === 'private-tours')
                                                            ? finalProductType
                                                            : (lastPathSegment === 'group-tours' || lastPathSegment === 'private-tours')
                                                                ? lastPathSegment
                                                                : undefined
                                                    }
                                                    searchDepartureDate={searchContext?.searchDepartureDate ?? undefined}
                                                    searchPaxCount={searchContext?.searchPaxCount}
                                                    searchChildCount={searchContext?.searchChildCount}
                                                />
                                            )
                                        })}
                                    </motion.div>
                                    {pagination && pagination.last_page > 1 && (
                                        <motion.ul
                                            className="flex flex-row gap-2
                                            [&>li]:flex [&>li]:items-center [&>li]:justify-center [&>li]:w-[45px] [&>li]:h-[45px] [&>li]:border [&>li]:border-[#EDEDED] [&>li]:rounded-full
                                            [&>li]:transition-all [&>li]:duration-200 [&>li]:ease-in-out
                                            [&>li]:hover:bg-alsero-first [&>li]:hover:text-white [&>li]:hover:border-alsero-first
                                            [&>li_a]:w-full [&>li_a]:h-full [&>li_a]:flex [&>li_a]:items-center [&>li_a]:justify-center
                                            [&>li_svg]:fill-alsero-dark-gray [&>li]:hover:[&_svg]:fill-white
                                            [&>li_svg]:transition-all [&>li_svg]:duration-200 [&>li_svg]:ease-in-out
                                            [&>li.disabled]:opacity-50 [&>li.disabled]:cursor-not-allowed [&>li.disabled]:hover:bg-transparent [&>li.disabled]:hover:text-alsero-dark-gray
                                            "
                                            variants={paginationVariants}
                                            initial="hidden"
                                            animate="visible"
                                        >
                                            <li className={pagination.page === 1 ? 'disabled' : ''}>
                                                <button type="button"
                                                    onClick={() => setPage(pagination.page - 1)}
                                                    disabled={pagination.page === 1}
                                                >
                                                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M6.4043 4.78516L10.123 1.06641C10.3965 0.792969 10.8066 0.792969 11.0527 1.06641L11.6816 1.66797C11.9277 1.94141 11.9277 2.35156 11.6816 2.59766L9.0293 5.22266L11.6816 7.875C11.9277 8.12109 11.9277 8.53125 11.6816 8.80469L11.0527 9.43359C10.8066 9.67969 10.3965 9.67969 10.123 9.43359L6.4043 5.71484C6.1582 5.44141 6.1582 5.03125 6.4043 4.78516ZM1.1543 5.71484C0.908203 5.44141 0.908203 5.03125 1.1543 4.78516L4.87305 1.06641C5.11914 0.792969 5.55664 0.792969 5.80273 1.06641L6.43164 1.66797C6.67773 1.94141 6.67773 2.35156 6.43164 2.59766L3.7793 5.25L6.43164 7.875C6.67773 8.12109 6.67773 8.55859 6.43164 8.80469L5.80273 9.43359C5.55664 9.67969 5.14648 9.67969 4.87305 9.43359L1.1543 5.71484Z"/>
                                                    </svg>
                                                </button>
                                            </li>
                                            {Array.from({ length: Math.min(pagination.last_page, 5) }, (_, i) => i + 1).map((page) => (
                                                <li
                                                    key={page}
                                                    className={page === pagination.page ? 'bg-alsero-first text-white' : ''}
                                                >
                                                    <button type="button" onClick={() => setPage(page)}>
                                                        {page}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={pagination.page === pagination.last_page ? 'disabled' : ''}>
                                                <button type="button"
                                                    onClick={() => setPage(pagination.page + 1)}
                                                    disabled={pagination.page === pagination.last_page}
                                                >
                                                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5.73047 5.21484L2.01172 8.93359C1.73828 9.20703 1.32812 9.20703 1.08203 8.93359L0.453125 8.33203C0.207031 8.05859 0.207031 7.64844 0.453125 7.40234L3.10547 4.77734L0.453125 2.125C0.207031 1.87891 0.207031 1.46875 0.453125 1.19531L1.08203 0.566406C1.32812 0.320312 1.73828 0.320312 2.01172 0.566406L5.73047 4.28516C5.97656 4.55859 5.97656 4.96875 5.73047 5.21484ZM10.9805 4.28516C11.2266 4.55859 11.2266 4.96875 10.9805 5.21484L7.26172 8.93359C7.01562 9.20703 6.57812 9.20703 6.33203 8.93359L5.70312 8.33203C5.45703 8.05859 5.45703 7.64844 5.70312 7.40234L8.35547 4.75L5.70312 2.125C5.45703 1.87891 5.45703 1.44141 5.70312 1.19531L6.33203 0.566406C6.57812 0.320312 6.98828 0.320312 7.26172 0.566406L10.9805 4.28516Z"/>
                                                    </svg>
                                                </button>
                                            </li>
                                        </motion.ul>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {category?.description && (
                        <div className="col-span-12 mt-16">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white rounded-lg shadow-tour-card border border-neutral-200/90 p-6"
                            >
                                <div 
                                    className="prose prose-sm max-w-none text-alsero-dark-gray"
                                    dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(category.description) }}
                                />
                            </motion.div>
                        </div>
                    )}
                </div>
            </div>

            {filterPortalReady && !isLg
                ? createPortal(
                      <AnimatePresence>
                          {mobileFiltersOpen ? (
                              <>
                                  <motion.div
                                      key="search-filter-backdrop"
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      transition={{ duration: 0.3 }}
                                      className="fixed inset-0 z-[10030] bg-alsero-text-dark/55 backdrop-blur-md"
                                      onClick={() => setMobileFiltersOpen(false)}
                                      aria-hidden
                                  />
                                  <motion.div
                                      key="search-filter-panel"
                                      initial={{ x: '-100%', opacity: 0 }}
                                      animate={{ x: 0, opacity: 1 }}
                                      exit={{ x: '-100%', opacity: 0 }}
                                      transition={{
                                          type: 'spring',
                                          damping: 32,
                                          stiffness: 320,
                                          opacity: { duration: 0.2 },
                                      }}
                                      className="fixed top-0 left-0 z-[10031] flex h-[100dvh] w-[min(100vw-1rem,24rem)] max-w-[min(100vw-1rem,32rem)] flex-col overflow-hidden border-r border-alsero-text-dark/10 bg-[linear-gradient(180deg,#fdfcfa_0%,#ffffff_45%,#f6f4f1_100%)] shadow-[8px_0_40px_-12px_rgba(27,31,28,0.18)]"
                                      role="dialog"
                                      aria-modal="true"
                                      aria-label={getVariable('search_filters_drawer_title', 'Filters')}
                                  >
                                      <div
                                          className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-alsero-first via-alsero-first/80 to-alsero-first/30"
                                          aria-hidden
                                      />
                                      <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-3 border-b-2 border-alsero-first/15 bg-white/90 px-4 py-4 backdrop-blur-md">
                                          <span className="text-lg font-semibold text-alsero-dark-gray">
                                              {getVariable('search_filters_drawer_title', 'Filters')}
                                          </span>
                                          <motion.button
                                              type="button"
                                              onClick={() => setMobileFiltersOpen(false)}
                                              whileHover={{ scale: 1.05 }}
                                              whileTap={{ scale: 0.95 }}
                                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-alsero-first/35 bg-white text-alsero-first shadow-sm transition-colors hover:bg-alsero-first hover:text-white"
                                              aria-label={getVariable('search_close_filters', 'Close filters')}
                                          >
                                              <svg
                                                  width="20"
                                                  height="20"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  aria-hidden
                                              >
                                                  <path
                                                      d="M18 6L6 18M6 6L18 18"
                                                      stroke="currentColor"
                                                      strokeWidth="2.5"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                  />
                                              </svg>
                                          </motion.button>
                                      </div>
                                      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-6 pt-2">
                                          <SidebarFilters
                                              filters={filters}
                                              activeFilters={activeFilters}
                                              updateFilter={updateFilter}
                                              getSuggestions={getSuggestions}
                                              isFiltersLoading={isFiltersLoading}
                                              clearSidebarFilters={clearSidebarFilters}
                                              hideCategorySlugs={slugToHideInSidebar ? [slugToHideInSidebar] : undefined}
                                              hideEntireCategoryFilterWhenMainCategory={!propCategorySlug && !!slugToHideInSidebar}
                                              hideLocationGroup
                                              hideFieldKeys={
                                                  finalProductType === 'tour' ||
                                                  finalProductType === 'private-tours' ||
                                                  finalProductType === 'group-tours'
                                                      ? ['tour_type']
                                                      : undefined
                                              }
                                          />
                                      </div>
                                  </motion.div>
                              </>
                          ) : null}
                      </AnimatePresence>,
                      document.body
                  )
                : null}
        </motion.main>
    )
}
